// Top-bar Megaphone bell.
//
// Click behaviour (per product spec):
//   • Fetch every unread NON-important approved-in-audience announcement.
//   • If none unread → navigate to /m/announcements.
//   • If one or more unread → open the OLDEST one in a modal right where
//     the user is (they don't leave their page).
//   • Clicking "I Read" marks it read via /mark-read and closes the modal —
//     the user is back on whatever page they were on. Clicking the bell
//     again opens the next-oldest unread.
//   • Close (X) just closes the modal without marking read — the item stays
//     unread and will reappear next time.
//
// Refreshes:
//   • On mount, every 90s, and on the custom 'announcements:unread-refresh'
//     event which is dispatched from all mark-read sites.

import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, X, CheckCircle2, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDateTimeIST } from "../lib/datetime";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AnnouncementsBell({ className = "" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [count, setCount] = useState(0);
  const [active, setActive] = useState(null);   // currently shown item
  const [busy, setBusy] = useState(false);

  const loadCount = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await api.get("/announcements/unread-count");
      setCount(Number(data?.count) || 0);
    } catch {
      /* silent */
    }
  }, [user?.id]);

  useEffect(() => { loadCount(); }, [loadCount]);
  useEffect(() => {
    if (!user?.id) return;
    const t = setInterval(loadCount, 90000);
    const onRefresh = () => loadCount();
    window.addEventListener("announcements:unread-refresh", onRefresh);
    return () => {
      clearInterval(t);
      window.removeEventListener("announcements:unread-refresh", onRefresh);
    };
  }, [user?.id, loadCount]);

  // On click: fetch full list, pick oldest unread, open modal — or navigate.
  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.get("/announcements?bucket=current");
      const list = (Array.isArray(data) ? data : []).filter(
        (a) => !a.important && a.status === "approved" && !a.i_read
      );
      if (list.length === 0) {
        nav("/m/announcements");
        return;
      }
      // Sort oldest-first by published_at (fallback created_at)
      list.sort((a, b) => {
        const ta = new Date(a.published_at || a.created_at).getTime();
        const tb = new Date(b.published_at || b.created_at).getTime();
        return ta - tb;
      });
      setActive(list[0]);
    } catch (e) {
      toast.error("Couldn't load announcements — please try again");
    } finally {
      setBusy(false);
    }
  };

  const markRead = async () => {
    if (!active) return;
    setBusy(true);
    try {
      await api.post(`/announcements/${active.id}/mark-read`);
      toast.success("Marked as read");
      setActive(null);
      // Refresh count + notify anyone listening
      await loadCount();
      window.dispatchEvent(new CustomEvent("announcements:unread-refresh"));
    } catch {
      toast.error("Couldn't mark as read");
    } finally {
      setBusy(false);
    }
  };

  const closeWithoutReading = () => setActive(null);

  const openInPage = () => {
    if (!active) return;
    const id = active.id;
    setActive(null);
    nav(`/m/announcements?ann=${id}`);
  };

  if (!user?.id) return null;

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        className={`relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/70 dark:bg-slate-800/70 backdrop-blur border border-slate-200 dark:border-slate-700 hover:bg-emerald-50 dark:hover:bg-emerald-500/15 hover:border-emerald-300 dark:hover:border-emerald-500/40 active:scale-95 hover:-translate-y-0.5 hover:shadow-md hover:shadow-emerald-200/40 dark:hover:shadow-emerald-500/20 transition-all duration-150 ${className}`}
        title={count > 0
          ? `${count} unread announcement${count === 1 ? "" : "s"} — click to open`
          : "Announcements"}
        data-testid="announcements-bell"
        data-keep-color
      >
        <Megaphone size={17} strokeWidth={2} className={count > 0 ? "text-emerald-600 dark:text-emerald-300" : "text-slate-600 dark:text-slate-300"} />
        {count > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-emerald-600 text-white text-[10px] font-extrabold flex items-center justify-center px-1 ring-2 ring-white dark:ring-slate-900 shadow-sm animate-pulse"
            data-testid="announcements-bell-count"
            data-keep-color
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      <Dialog open={!!active} onOpenChange={(v) => !v && closeWithoutReading()}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ann-bell-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Megaphone className="w-5 h-5 text-emerald-600" />
                  {active.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="bg-amber-100 text-amber-900 border-amber-300">Unread</Badge>
                  {count > 1 && (
                    <Badge variant="secondary" className="bg-slate-100 text-slate-700">
                      {count - 1} more unread after this
                    </Badge>
                  )}
                </div>
                <div className="text-[12px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>By <strong>{active.created_by_name}</strong></span>
                  {active.created_by_designation && <span>· {active.created_by_designation}</span>}
                  <span>· Posted {fmtDateTimeIST(active.published_at || active.created_at)}</span>
                  <span>· Ends {fmtDateTimeIST(active.end_at)}</span>
                </div>
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 whitespace-pre-wrap text-slate-800">
                  {active.body || <span className="text-slate-400">(no body)</span>}
                </div>
                {active.external_url && (
                  <a
                    href={active.external_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-emerald-700 hover:underline text-[13px]"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> {active.external_url}
                  </a>
                )}
                {active.attachment_id && (
                  <a
                    href={`${BACKEND}/api/announcements/${active.id}/attachment`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 hover:bg-slate-50 text-[13px]"
                  >
                    Download {active.attachment_name}
                  </a>
                )}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={openInPage} data-testid="ann-bell-open-page">
                  Open in Announcements
                </Button>
                <Button variant="outline" onClick={closeWithoutReading} data-testid="ann-bell-close">
                  <X className="w-4 h-4 mr-1.5" /> Close (keep unread)
                </Button>
                <Button
                  onClick={markRead}
                  disabled={busy}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="ann-bell-read"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> I Read
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
